import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  storeProducts,
  productsByCategory,
  isAvailable,
  type StoreProduct,
} from '../data/storeProducts';

const categoryLabel: Record<StoreProduct['category'], string> = {
  tops: 'Tops',
  bottoms: 'Bottoms',
  outerwear: 'Outerwear',
  accessories: 'Accessories',
};

const categoryOrder: StoreProduct['category'][] = [
  'tops',
  'outerwear',
  'bottoms',
  'accessories',
];

const ProductCard: React.FC<{ product: StoreProduct; index: number }> = ({
  product,
  index,
}) => {
  const hasBackImage = product.images.length > 1;
  const available = isAvailable(product);

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.6, delay: (index % 4) * 0.06, ease: [0.22, 1, 0.36, 1] }}
      className="group relative"
    >
      <Link to={`/store/${product.slug}`} className="block">
        <div className="relative aspect-[4/5] overflow-hidden rounded-2xl bg-[#0E1422] border border-white/[0.08] group-hover:border-white/25 transition-colors duration-500">
          <img
            src={product.images[0]}
            alt={product.name}
            className={`absolute inset-0 w-full h-full object-cover transition-all duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
              hasBackImage
                ? 'group-hover:opacity-0 group-hover:scale-[1.04]'
                : 'group-hover:scale-[1.04]'
            }`}
            loading="lazy"
          />

          {hasBackImage && (
            <img
              src={product.images[1]}
              alt=""
              className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
              loading="lazy"
            />
          )}

          {!available && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="relative">
                {/* outer thin ring */}
                <div className="absolute -inset-1.5 sm:-inset-2 rounded-full border border-white/20" />
                {/* main badge */}
                <div className="relative px-4 sm:px-6 py-2 sm:py-3 rounded-full bg-black/55 backdrop-blur-md border border-white/40 shadow-[0_8px_30px_rgba(0,0,0,0.35)]">
                  <span className="text-[9px] sm:text-[11px] uppercase tracking-[0.28em] sm:tracking-[0.4em] text-white font-semibold whitespace-nowrap">
                    Coming Soon
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-5 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 className="text-white font-display font-semibold text-base leading-snug group-hover:text-neutral-gray-300 transition-colors duration-500">
              {product.name}
            </h3>
            {product.color && (
              <p className="mt-1 text-neutral-gray-400 text-sm leading-snug">
                ({product.color})
              </p>
            )}
          </div>
          {product.price !== null ? (
            <p className="text-white font-display font-semibold text-base whitespace-nowrap tabular-nums">
              ${product.price}
            </p>
          ) : (
            <p className="text-neutral-gray-500 text-[10px] uppercase tracking-[0.2em] whitespace-nowrap">
              TBD
            </p>
          )}
        </div>
      </Link>
    </motion.div>
  );
};

const ScrollPlayVideo: React.FC = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hasPlayedRef = useRef(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (
            entry.isIntersecting &&
            entry.intersectionRatio >= 0.5 &&
            !hasPlayedRef.current
          ) {
            hasPlayedRef.current = true;

            // On mobile, skip the first ~3.5s so the video lands on the action sooner
            const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
            const startVideo = () => {
              if (isMobile) {
                try {
                  video.currentTime = 3.5;
                } catch {
                  /* seek failed, fall through to plain play */
                }
              }
              const playPromise = video.play();
              if (playPromise && typeof playPromise.catch === 'function') {
                playPromise.catch(() => {
                  /* Autoplay blocked, leave video paused at first frame */
                });
              }
            };

            // Need metadata loaded before seeking is reliable
            if (video.readyState >= 1) {
              startVideo();
            } else {
              video.addEventListener('loadedmetadata', startVideo, { once: true });
            }
          }
        });
      },
      { threshold: [0.5] }
    );

    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="relative bg-black border-y border-white/10">
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full overflow-hidden bg-[#0E1422] h-[68vh] min-h-[440px] max-h-[720px]"
      >
        <video
          ref={videoRef}
          src="/store/bunny-walking.mp4"
          muted
          playsInline
          preload="metadata"
          className="w-full h-full object-cover"
        />
      </motion.div>
    </section>
  );
};

const CategorySection: React.FC<{ category: StoreProduct['category'] }> = ({
  category,
}) => {
  const items = productsByCategory(category);
  if (items.length === 0) return null;

  return (
    <section
      id={`cat-${category}`}
      className="scroll-mt-24 py-16 lg:py-20 border-t border-white/10 first:border-t-0"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-baseline justify-between mb-10">
          <h2 className="font-display font-black text-2xl sm:text-3xl lg:text-4xl tracking-[-0.02em]">
            {categoryLabel[category]}
          </h2>
          <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-gray-500 tabular-nums">
            {items.length.toString().padStart(2, '0')} pieces
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-6 gap-y-14 lg:gap-x-8">
          {items.map((product, idx) => (
            <ProductCard key={product.slug} product={product} index={idx} />
          ))}
        </div>
      </div>
    </section>
  );
};

const Store: React.FC = () => {
  const [mounted, setMounted] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMounted(true);
    if (!location.hash) {
      window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
    } else {
      const el = document.querySelector(location.hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
      }
    }
  }, [location.hash]);

  return (
    <div className="relative bg-black min-h-screen text-white selection:bg-white selection:text-black overflow-hidden">
      {/* HERO - full bleed, no spacer */}
      <section className="relative overflow-hidden flex flex-col min-h-[480px] sm:min-h-[600px] lg:min-h-[700px]">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1428] via-black to-black pointer-events-none" />

        {/* Crosses pattern - hero only, faded at edges */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><path d='M80 70 L80 90 M70 80 L90 80' stroke='white' stroke-width='1' fill='none' opacity='0.16'/></svg>\")",
            backgroundSize: '160px 160px',
            maskImage:
              'radial-gradient(ellipse 75% 55% at 50% 45%, black 0%, black 35%, transparent 95%)',
            WebkitMaskImage:
              'radial-gradient(ellipse 75% 55% at 50% 45%, black 0%, black 35%, transparent 95%)',
          }}
        />

        {/* Eyebrow top */}
        <div className="relative z-10 pt-28 lg:pt-32">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between gap-3">
            <p className="text-[9px] sm:text-[10px] uppercase tracking-[0.28em] sm:tracking-[0.4em] text-neutral-gray-400 whitespace-nowrap">
              Est. 2023
            </p>
            <p className="text-[9px] sm:text-[10px] uppercase tracking-[0.28em] sm:tracking-[0.4em] text-neutral-gray-400 whitespace-nowrap">
              The Enhance Collection
            </p>
          </div>
        </div>

        {/* Centered horse, fully visible */}
        <div className="relative flex-1 flex items-center justify-center px-2 sm:px-6 pt-2 pb-6 sm:pt-4 sm:pb-10 lg:pt-6 lg:pb-12">
          <img
            src="/store/horse-hero.webp"
            alt="Enhance Companies"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className="relative w-[128%] sm:w-full max-w-[980px] h-auto select-none"
            style={{
              filter: 'invert(1) brightness(1.05) contrast(1.05)',
              mixBlendMode: 'screen',
            }}
          />
        </div>

        {/* (tagline moved to the consolidated section below) */}
      </section>

      {/* CONSOLIDATED: tagline + category index, single compact section */}
      <section className="relative bg-black border-y border-white/10">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 py-14 lg:py-20 text-center">
          {/* Eyebrow flanked by hairlines */}
          <div className="flex items-center gap-5 justify-center mb-7">
            <span className="block h-px w-12 sm:w-16 bg-white/25" />
            <span className="text-[10px] uppercase tracking-[0.45em] text-white/70 whitespace-nowrap">
              Collection No. 01
            </span>
            <span className="block h-px w-12 sm:w-16 bg-white/25" />
          </div>

          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl leading-[0.95] tracking-[-0.03em]">
            Built for the
            {' '}
            <span className="italic font-light text-neutral-gray-300">
              closers.
            </span>
          </h2>

          <p className="mt-5 text-sm text-neutral-gray-400 max-w-md mx-auto leading-relaxed">
            Apparel and accessories for the producers who set the standard.
          </p>

          {/* Hairline divider */}
          <div className="mt-10 mb-10 mx-auto w-16 h-px bg-white/20" />

          {/* Categories grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 sm:gap-x-6 gap-y-10 sm:gap-y-0">
            {categoryOrder.map((cat, idx) => {
              const count = productsByCategory(cat).length;
              if (count === 0) return null;
              return (
                <a
                  key={cat}
                  href={`#cat-${cat}`}
                  className="group flex flex-col items-center text-center transition-all duration-500 hover:-translate-y-0.5"
                >
                  <p className="text-[9px] sm:text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2 tabular-nums">
                    {String(idx + 1).padStart(2, '0')}
                  </p>
                  <p className="font-display font-semibold text-sm sm:text-base lg:text-lg text-white tracking-[-0.01em] group-hover:text-neutral-gray-200 transition-colors">
                    {categoryLabel[cat]}
                  </p>
                  <span className="block mt-2 h-px w-5 bg-white/20 group-hover:w-10 group-hover:bg-white/70 transition-all duration-500" />
                  <p className="mt-2 text-[9px] sm:text-[10px] uppercase tracking-[0.25em] text-white/50 tabular-nums whitespace-nowrap">
                    {count.toString().padStart(2, '0')} {count === 1 ? 'Piece' : 'Pieces'}
                  </p>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* CATEGORY SECTIONS */}
      <main>
        {categoryOrder.map((cat) => (
          <CategorySection key={cat} category={cat} />
        ))}
      </main>

      {/* BRAND VIDEO - above closing, plays on scroll, stops at last frame */}
      <ScrollPlayVideo />

      {/* CLOSING STATEMENT */}
      <section className="relative overflow-hidden border-t border-white/10 bg-black min-h-[460px] sm:min-h-[440px]">
        {/* EC Paddles
              mobile: centered horizontally, slightly above bottom, brighter
              desktop: anchored right, vertically centered with offset */}
        <motion.img
          src="/store/ec-paddles.webp"
          alt=""
          aria-hidden="true"
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 0.96, scale: 1 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
          className="pointer-events-none absolute select-none max-w-none
            left-0 -translate-x-[18%] bottom-3 w-[100%]
            sm:left-auto sm:right-0 sm:translate-x-[10%] sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2 sm:-mt-24 sm:w-[820px]
            lg:w-[1080px] lg:-mt-28"
        />

        {/* Vignette: vertical on mobile (text up top, paddles brighten toward bottom), horizontal on desktop */}
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-black via-black/45 to-transparent sm:bg-gradient-to-r sm:from-black sm:via-black/70 sm:to-transparent" />

        {/* Crosses pattern faded in the background */}
        <div
          className="absolute inset-0 pointer-events-none hidden sm:block"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='180' height='180'><path d='M90 80 L90 100 M80 90 L100 90' stroke='white' stroke-width='1' fill='none' opacity='0.1'/></svg>\")",
            backgroundSize: '180px 180px',
            maskImage:
              'linear-gradient(to right, black 0%, black 40%, transparent 80%)',
            WebkitMaskImage:
              'linear-gradient(to right, black 0%, black 40%, transparent 80%)',
          }}
        />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-10 pb-56 sm:py-16 lg:py-24">
          <div className="max-w-xl">
            {/* Eyebrow with hairline accent */}
            <div className="flex items-center gap-4 mb-6">
              <span className="block h-px w-10 bg-white/40" />
              <p className="text-[10px] uppercase tracking-[0.4em] text-neutral-gray-300">
                Enhance Companies
              </p>
            </div>

            <h3 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[0.92] tracking-[-0.03em]">
              Wear the
              {' '}
              <span className="italic font-light text-neutral-gray-300">
                standard.
              </span>
            </h3>

            <div className="mt-8 flex items-center gap-5">
              <span className="block h-px w-12 bg-white/40" />
              <p className="text-[10px] uppercase tracking-[0.35em] text-neutral-gray-300 tabular-nums">
                {storeProducts.length.toString().padStart(2, '0')} pieces.
                Current collection.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Store;
